/**
 * Admin 测算台 — 指导销售×提现 + SP1 分桶 + 迁移对照
 */
import { useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Space,
  Statistic,
  Table,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { api, ApiError } from '../../api/client';

type GuideResult = {
  register_date: string;
  register_date_from?: string;
  register_date_to?: string;
  source: string;
  guide_unit_usd: string;
  ep1_ratio: string;
  account_count: number;
  guide_total_usd: string;
  ep1_forecast_usd: string;
  quota_usd_per_user?: string;
  note_zh: string;
};

type Sp1Result = {
  source: string;
  metric: string;
  total: number;
  bands: Array<{ label: string; min: string; max: string | null; count: number }>;
  note_zh: string;
};

type MigrationResult = {
  node_user_count: number;
  php_user_count: number | null;
  unmirrored_sample: Array<{ id: number; username: string }>;
  note_zh: string;
};

export function ForecastPage() {
  const [guideLoading, setGuideLoading] = useState(false);
  const [guide, setGuide] = useState<GuideResult | null>(null);
  const [sp1Loading, setSp1Loading] = useState(false);
  const [sp1, setSp1] = useState<Sp1Result | null>(null);
  const [migLoading, setMigLoading] = useState(false);
  const [mig, setMig] = useState<MigrationResult | null>(null);
  const [guideForm] = Form.useForm();
  const [sp1Form] = Form.useForm();

  async function runGuide(values: {
    register_range: [dayjs.Dayjs, dayjs.Dayjs];
    guide_unit_usd: number;
    ep1_ratio: number;
    source: 'node' | 'php';
  }) {
    setGuideLoading(true);
    try {
      const [from, to] = values.register_range;
      const data = await api.get<GuideResult>('/api/admin/forecast/guide-sales-withdraw', {
        register_date_from: from.format('YYYY-MM-DD'),
        register_date_to: to.format('YYYY-MM-DD'),
        guide_unit_usd: String(values.guide_unit_usd),
        ep1_ratio: String(values.ep1_ratio),
        source: values.source,
      });
      setGuide(data);
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '测算失败');
    } finally {
      setGuideLoading(false);
    }
  }

  async function runSp1(values: { source: 'node' | 'php'; bands: string }) {
    setSp1Loading(true);
    try {
      const data = await api.get<Sp1Result>('/api/admin/forecast/sp1-bands', {
        source: values.source,
        bands: values.bands,
      });
      setSp1(data);
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '分桶失败');
    } finally {
      setSp1Loading(false);
    }
  }

  async function runMigration() {
    setMigLoading(true);
    try {
      const data = await api.get<MigrationResult>('/api/admin/forecast/migration-report');
      setMig(data);
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '加载失败');
    } finally {
      setMigLoading(false);
    }
  }

  return (
    <div>
      <Typography.Title level={4}>测算台</Typography.Title>
      <Typography.Paragraph type="secondary">
        会议测算：指导销售×静态 EP1 提现比例；SP1 静态封顶分桶（默认 300/500/800）；以及 Node vs PHP 用户对照。
      </Typography.Paragraph>

      <Card title="① 指导销售 × 提现（EP1）" style={{ marginBottom: 16 }}>
        <Form
          form={guideForm}
          layout="inline"
          initialValues={{
            register_range: [dayjs('2025-06-23'), dayjs()],
            guide_unit_usd: 100,
            ep1_ratio: 0.52,
            source: 'node',
          }}
          onFinish={runGuide}
        >
          <Form.Item name="register_range" label="注册区间" rules={[{ required: true }]}>
            <DatePicker.RangePicker />
          </Form.Item>
          <Form.Item name="guide_unit_usd" label="指导单价 $" rules={[{ required: true }]}>
            <InputNumber min={0} step={1} />
          </Form.Item>
          <Form.Item name="ep1_ratio" label="EP1 比例" rules={[{ required: true }]}>
            <InputNumber min={0} max={1} step={0.01} />
          </Form.Item>
          <Form.Item name="source" label="数据源">
            <Radio.Group
              options={[
                { value: 'node', label: 'Node' },
                { value: 'php', label: 'PHP' },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={guideLoading}>
              测算
            </Button>
          </Form.Item>
        </Form>
        {guide && (
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={6}>
              <Statistic title="账户数" value={guide.account_count} />
            </Col>
            <Col span={6}>
              <Statistic title="指导总额 USD" value={guide.guide_total_usd} />
            </Col>
            <Col span={6}>
              <Statistic title="EP1 测算额 USD" value={guide.ep1_forecast_usd} />
            </Col>
            <Col span={6}>
              <Statistic title="每户额度 $" value={guide.quota_usd_per_user ?? '—'} />
            </Col>
            <Col span={24} style={{ marginTop: 8 }}>
              <Typography.Text type="secondary">
                区间 {guide.register_date_from ?? guide.register_date}
                {guide.register_date_to && guide.register_date_to !== guide.register_date_from
                  ? ` ～ ${guide.register_date_to}`
                  : ''}{' '}
                · 比例 {guide.ep1_ratio} · {guide.note_zh}
              </Typography.Text>
            </Col>
          </Row>
        )}
      </Card>

      <Card title="② SP1 分桶（300 / 500 / 800）" style={{ marginBottom: 16 }}>
        <Form
          form={sp1Form}
          layout="inline"
          initialValues={{ source: 'node', bands: '300,500,800' }}
          onFinish={runSp1}
        >
          <Form.Item name="source" label="数据源">
            <Radio.Group
              options={[
                { value: 'node', label: 'Node 静态封顶' },
                { value: 'php', label: 'PHP net_weight' },
              ]}
            />
          </Form.Item>
          <Form.Item name="bands" label="桶边界">
            <Input style={{ width: 180 }} placeholder="300,500,800" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={sp1Loading}>
              统计
            </Button>
          </Form.Item>
        </Form>
        {sp1 && (
          <>
            <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
              {sp1.note_zh} · 指标 {sp1.metric} · 合计 {sp1.total}
            </Typography.Paragraph>
            <Table
              size="small"
              pagination={false}
              rowKey="label"
              dataSource={sp1.bands}
              columns={[
                { title: '分桶', dataIndex: 'label' },
                { title: '下限', dataIndex: 'min' },
                { title: '上限', dataIndex: 'max', render: (v) => v ?? '∞' },
                { title: '账户数', dataIndex: 'count' },
              ]}
            />
          </>
        )}
      </Card>

      <Card title="③ 迁移对照（Node vs PHP）">
        <Space>
          <Button type="primary" loading={migLoading} onClick={() => void runMigration()}>
            刷新对照
          </Button>
        </Space>
        {mig && (
          <>
            <Descriptions style={{ marginTop: 16 }} column={2} size="small" bordered>
              <Descriptions.Item label="Node users">{mig.node_user_count}</Descriptions.Item>
              <Descriptions.Item label="PHP php_user_user">
                {mig.php_user_count ?? '—'}
              </Descriptions.Item>
            </Descriptions>
            <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
              {mig.note_zh}
            </Typography.Paragraph>
            {mig.unmirrored_sample.length > 0 && (
              <Table
                size="small"
                pagination={false}
                rowKey="id"
                dataSource={mig.unmirrored_sample}
                columns={[
                  { title: 'ID', dataIndex: 'id', width: 80 },
                  { title: '未镜像用户名', dataIndex: 'username' },
                ]}
              />
            )}
          </>
        )}
      </Card>
    </div>
  );
}
